<template>
    <div class="google-signin-wrapper w-100"
         :style="{ maxWidth: computedWidth + 'px', minHeight: '40px' }">
        <b-button variant="outline-light"
                  class="w-100 d-flex align-items-center justify-content-between sso-btn px-3"
                  :style="{ maxWidth: computedWidth + 'px', height: '40px', borderRadius: shape === 'pill' ? '9999px' : '4px', fontSize: '14px' }"
                  :disabled="loading"
                  data-test="btn-signin-google"
                  aria-label="Continue with Google"
                  title="Continue with Google"
                  @click="onGoogleClick">
            <div class="d-flex align-items-center">
                <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                <svg v-else class="mr-2 flex-shrink-0" width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"/>
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.35 24 12 24z"/>
                    <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 10.02 0 12s.45 3.82 1.25 5.42l4.03-3.15z"/>
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.35 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
                </svg>
                <span class="text-truncate">{{ buttonLabel }}</span>
            </div>
            <div v-if="effectiveAccount" class="d-flex align-items-center ml-2" data-test="avatar-google-account" :title="'Last used: ' + (effectiveAccount.name || effectiveAccount.email)">
                <b-avatar :src="effectiveAccount.picture" :text="getInitials(effectiveAccount.name, effectiveAccount.email)" size="24px" class="border border-secondary"></b-avatar>
            </div>
        </b-button>
    </div>
</template>

<script>
    import { eventBus } from '../event-bus';
    import toastMixin from "../mixinToast";
    import accountMixin from "../mixinAccount";

    window['onGoogleSigninResponse'] = async function(response) {                
        eventBus.$emit('onGoogleSigninResponse', response?.credential || response);
    };
    window['onGoogleSigninError'] = async function(response) {                
        eventBus.$emit('onGoogleSigninError', response?.credential || response);
    };

    export default {
        mixins: [toastMixin, accountMixin],
        name: 'GoogleSignInButton',
        props: {
            clientId: String,
            rememberedAccount: {
                type: Object,
                default: null
            },
            emitTokenOnly: {
                type: Boolean,
                default: false
            },
            theme: {
                type: String,
                default: 'filled_black'
            },
            shape: {
                type: String,
                default: 'rectangular'
            },
            text: {
                type: String,
                default: ''
            },
            width: {
                type: [Number, String],
                default: 320
            },
            suppressToast: {
                type: Boolean,
                default: false
            },
            invitationToken: {
                type: String,
                default: null
            }
        },
        data() {
            return {
                loading: false,
                tokenClient: null,
                cachedAccount: null
            };
        },
        computed: {
            effectiveAccount() {
                if (this.rememberedAccount && (this.rememberedAccount.picture || this.rememberedAccount.name || this.rememberedAccount.email)) {
                    return this.rememberedAccount;
                }
                if (this.cachedAccount && (this.cachedAccount.picture || this.cachedAccount.name || this.cachedAccount.email)) {
                    return this.cachedAccount;
                }
                return null;
            },
            effectiveClientId() {
                return (this.clientId || process.env.VUE_APP_GOOGLE_CLIENT_ID || '').trim();
            },
            computedWidth() {
                const w = parseInt(this.width, 10);
                return isNaN(w) || w <= 0 ? 320 : Math.min(400, Math.max(200, w));
            },
            computedText() {
                if (this.text) return this.text;
                return 'continue_with';
            },
            buttonLabel() {
                if (this.computedText === 'continue_with') return 'Continue with Google';
                if (this.computedText === 'signup_with') return 'Sign up with Google';
                return 'Sign in with Google';
            }
        },
        mounted() {
            eventBus.$on('onGoogleSigninResponse', this.onIdTokenResponse);
            eventBus.$on('onGoogleSigninError', this.onResponseError);
            this.loadCachedAccount();
            this.initGoogleAuth();
        },
        beforeUnmount() {
            eventBus.$off('onGoogleSigninResponse', this.onIdTokenResponse);
            eventBus.$off('onGoogleSigninError', this.onResponseError);
        },
        methods: {
            initGoogleAuth() {
                if (!this.effectiveClientId) return;
                try {
                    if (window.google?.accounts?.id) {
                        window.google.accounts.id.initialize({
                            client_id: this.effectiveClientId,
                            callback: (response) => {
                                if (response && response.credential) {
                                    this.onIdTokenResponse(response.credential);
                                }
                            },
                            error_callback: (error) => {
                                this.onResponseError(error);
                            },
                            auto_select: false,
                            cancel_on_tap_outside: true
                        });
                    }
                } catch (e) {
                    // ignore
                }
            },
            onGoogleClick() {
                if (!this.effectiveClientId) {
                    this.toastError("Google Client ID is not configured.");
                    return;
                }

                if (window.google?.accounts?.oauth2) {
                    if (!this.tokenClient) {
                        this.tokenClient = window.google.accounts.oauth2.initTokenClient({
                            client_id: this.effectiveClientId,
                            scope: 'openid email profile',
                            callback: async (tokenResponse) => {
                                if (tokenResponse?.error) {
                                    this.onResponseError(tokenResponse.error_description || tokenResponse.error);
                                    return;
                                }
                                if (tokenResponse?.access_token) {
                                    await this.onAccessTokenResponse(tokenResponse.access_token);
                                }
                            },
                            error_callback: (error) => {
                                this.onResponseError(error);
                            }
                        });
                    }
                    this.tokenClient.requestAccessToken({ prompt: 'select_account' });
                    return;
                }

                if (window.google?.accounts?.id) {
                    window.google.accounts.id.prompt();
                    return;
                }

                this.toastError("Google Sign-In is initializing. Please try again in a moment.");
            },
            async onAccessTokenResponse(accessToken) {
                if (this.emitTokenOnly) {
                    this.$emit('id-token', { accessToken });
                    return;
                }

                this.$store.commit('setSigningIn', true);
                this.loading = true;
                const formData = new FormData();
                formData.append('accessToken', accessToken);
                if (this.invitationToken) {
                    formData.append('token', this.invitationToken);
                }
                try {
                    const resp = await this.$http.post('/Account/VerifyGoogleSignIn', formData);
                    const account = resp.data;
                    const savedAccount = {
                        email: account.email,
                        name: account.fullName,
                        picture: account.picture,
                        provider: 'Google',
                        lastLogin: new Date().toISOString()
                    };
                    try {
                        localStorage.setItem('velocytrack_last_google_account', JSON.stringify(savedAccount));
                        this.cachedAccount = savedAccount;
                    } catch (_) {
                        // Ignore localStorage errors
                    }
                    this.$store.commit('setAccount', account);
                    this.$store.commit('setAuthenticated', true);
                    await this.initSessionConfigAsync();
                    this.$emit('success', account);
                    this.$router.push('/timelog').catch(() => {});
                } catch (err) {
                    const respData = err.response?.data;
                    if (respData && respData.code === 'BETA_NOT_REGISTERED') {
                        this.$emit('beta-claim-pending', respData);
                        return;
                    }
                    const msg = respData?.message || 
                                (typeof err.response?.data === 'string' ? err.response.data : null) || 
                                err.message || 
                                'Google sign in failed. Please try again.';
                    this.$emit('error', msg);
                    if (!this.suppressToast) {
                        this.toastError(msg);
                    }
                } finally {
                    this.$store.commit('setSigningIn', false);
                    this.loading = false;
                }
            },
            async onIdTokenResponse(credential) {
                if (this.emitTokenOnly) {
                    this.$emit('id-token', credential);
                    return;
                }

                this.$store.commit('setSigningIn', true);
                this.loading = true;
                const formData = new FormData();
                formData.append('idToken', credential);
                if (this.invitationToken) {
                    formData.append('token', this.invitationToken);
                }
                try {
                    const resp = await this.$http.post('/Account/VerifyGoogleSignIn', formData);
                    const account = resp.data;
                    const savedAccount = {
                        email: account.email,
                        name: account.fullName,
                        picture: account.picture,
                        provider: 'Google',
                        lastLogin: new Date().toISOString()
                    };
                    try {
                        localStorage.setItem('velocytrack_last_google_account', JSON.stringify(savedAccount));
                        this.cachedAccount = savedAccount;
                    } catch (_) {
                        // Ignore localStorage errors
                    }
                    this.$store.commit('setAccount', account);
                    this.$store.commit('setAuthenticated', true);
                    await this.initSessionConfigAsync();
                    this.$emit('success', account);
                    this.$router.push('/timelog').catch(() => {});
                } catch (err) {
                    const respData = err.response?.data;
                    if (respData && respData.code === 'BETA_NOT_REGISTERED') {
                        this.$emit('beta-claim-pending', respData);
                        return;
                    }
                    const msg = respData?.message || 
                                (typeof err.response?.data === 'string' ? err.response.data : null) || 
                                err.message || 
                                'Google sign in failed. Please try again.';
                    this.$emit('error', msg);
                    if (!this.suppressToast) {
                        this.toastError(msg);
                    }
                } finally {
                    this.$store.commit('setSigningIn', false);
                    this.loading = false;
                }
            },
            async onResponseError(error) {                
                const errDetail = typeof error === 'string' ? error : (error?.type || error?.message || 'Authentication failed');
                const msg = "Error during Google Sign-In: " + errDetail;
                this.$emit('error', msg);
                if (!this.suppressToast) {
                    this.toastError(msg);
                }
                this.$store.commit('setSigningIn', false);
                this.loading = false;
            },
            loadCachedAccount() {
                try {
                    const g = localStorage.getItem('velocytrack_last_google_account');
                    if (g) {
                        this.cachedAccount = JSON.parse(g);
                    }
                } catch (_) {
                    // Ignore localStorage errors
                }
            },
            getInitials(name, email) {
                const text = name || email || '';
                if (!text) return 'U';
                const parts = text.trim().split(/\s+/);
                if (parts.length >= 2) {
                    return (parts[0][0] + parts[1][0]).toUpperCase();
                }
                return text.slice(0, 2).toUpperCase();
            }
        }
    };
</script>

<style scoped>
    .google-signin-wrapper {
        min-height: 40px;
    }

    .sso-btn {
        background-color: #131314;
        border: 1px solid #3c4043;
        color: #e3e3e3;
        font-size: 14px;
        font-weight: 500;
        height: 40px;
        border-radius: 4px;
        box-sizing: border-box;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }

    .sso-btn:hover {
        background-color: #202124;
        border-color: #5f6368;
        color: #ffffff;
    }

    .sso-btn:active {
        background-color: #303134;
    }
</style>
